// src/pages/TriagePage.tsx

import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CameraCapture } from '../components/CameraCapture';
import { CountdownTimer } from '../components/CountdownTimer';
import { VitalSummaryCard } from '../components/VitalSummaryCard';
import { MEASUREMENT_CONFIG } from '../config/constants';
import { extractROI, extractMeanGreenValue, processRppgSignal } from '../lib/rppg';
import {
  initializePoseLandmarker,
  disposePoseLandmarker,
  classifyPosture,
  computeRespiratoryEffort,
  computeRespiratoryRate,
  aggregatePostureResults,
} from '../lib/pose';
import {
  initializeFaceLandmarker,
  disposeFaceLandmarker,
  computeEyeOpenRatio,
  computePainScore,
  aggregateFaceFeatures,
} from '../lib/face_features';
import {
  extractFaceROI,
  computePallorScore,
  detectSweat,
  aggregateSkinFeatures,
} from '../lib/skin_features';
import { generatePatientId } from '../lib/uuid';
import { postToOpusWithRetry, ApiError } from '../lib/api';
import type {
  TriageRequestPayload,
  Vitals,
  Posture,
  FaceFeatures,
  PostureLabel,
} from '../types/triage';

type MeasurementPhase = 'idle' | 'ready' | 'measuring' | 'processing' | 'results' | 'sending';

export function TriagePage() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState<MeasurementPhase>('idle');
  const [error, setError] = useState<string>('');
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [secondsRemaining, setSecondsRemaining] = useState(10);

  // Measurement results
  const [vitals, setVitals] = useState<Vitals>({
    hr_bpm: null,
    rr_bpm: null,
    hr_confidence: null,
    rr_confidence: null,
  });
  const [posture, setPosture] = useState<Posture>({ label: 'unknown', confidence: null });
  const [respiratoryEffortScore, setRespiratoryEffortScore] = useState(0);
  const [faceFeatures, setFaceFeatures] = useState<FaceFeatures>({});

  // Refs for video processing
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null); // For ROI visualization
  const streamRef = useRef<MediaStream | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  // Data collection arrays
  const gSignalRef = useRef<number[]>([]);
  const postureLabelsRef = useRef<PostureLabel[]>([]);
  const shoulderYTimeSeriesRef = useRef<number[]>([]);
  const eyeOpenRatiosRef = useRef<number[]>([]);
  const painScoresRef = useRef<number[]>([]);
  const pallorScoresRef = useRef<number[]>([]);
  const sweatFlagsRef = useRef<boolean[]>([]);

  const frameCountRef = useRef(0);
  const startTimeRef = useRef(0);

  const handleStreamReady = useCallback((stream: MediaStream, video: HTMLVideoElement) => {
    streamRef.current = stream;
    videoRef.current = video;
    setPhase('ready');
    setError('');
  }, []);

  const handleCameraError = useCallback((errorMessage: string) => {
    setError(errorMessage);
  }, []);

  const startMeasurement = async () => {
    if (!videoRef.current) return;

    console.log('Starting measurement...');

    // Reset data
    gSignalRef.current = [];
    postureLabelsRef.current = [];
    shoulderYTimeSeriesRef.current = [];
    eyeOpenRatiosRef.current = [];
    painScoresRef.current = [];
    pallorScoresRef.current = [];
    sweatFlagsRef.current = [];
    frameCountRef.current = 0;

    setPhase('measuring');
    setSecondsRemaining(10);
    console.log('Measurement duration:', MEASUREMENT_CONFIG.DURATION_MS, 'ms');

    // Wait for video to have valid dimensions
    if (videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
      console.warn('Video dimensions not ready, waiting...');
      await new Promise<void>((resolve) => {
        const checkDimensions = () => {
          if (videoRef.current && videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0) {
            resolve();
          } else {
            setTimeout(checkDimensions, 50);
          }
        };
        checkDimensions();
      });
    }

    console.log('Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);

    // Create canvas
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvasRef.current = canvas;

    // Initialize MediaPipe
    const poseLandmarker = await initializePoseLandmarker();
    const faceLandmarker = await initializeFaceLandmarker();

    startTimeRef.current = performance.now();

    const processFrame = async () => {
      if (!videoRef.current || !canvasRef.current) return;

      const now = performance.now();
      const elapsed = now - startTimeRef.current;

      if (elapsed >= MEASUREMENT_CONFIG.DURATION_MS) {
        // Measurement complete
        console.log('Measurement time complete! Elapsed:', elapsed, 'ms');
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        await processMeasurementData();
        return;
      }

      // Update countdown
      const remaining = Math.ceil((MEASUREMENT_CONFIG.DURATION_MS - elapsed) / 1000);
      setSecondsRemaining(remaining);

      const ctx = canvasRef.current.getContext('2d')!;
      ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

      // rPPG: every frame
      const roi = extractROI(canvas.width, canvas.height);
      const greenValue = extractMeanGreenValue(ctx, roi);
      gSignalRef.current.push(greenValue);

      // Draw ROI overlay on video
      if (overlayCanvasRef.current) {
        const overlayCtx = overlayCanvasRef.current.getContext('2d');
        if (overlayCtx) {
          overlayCtx.clearRect(0, 0, overlayCanvasRef.current.width, overlayCanvasRef.current.height);
          overlayCtx.strokeStyle = '#00ff00';
          overlayCtx.lineWidth = 2;
          overlayCtx.strokeRect(roi.x, roi.y, roi.width, roi.height);

          // Add label
          overlayCtx.fillStyle = '#00ff00';
          overlayCtx.font = '14px monospace';
          overlayCtx.fillText('rPPG ROI', roi.x, roi.y - 5);
        }
      }

      // Pose: every 10 frames
      if (frameCountRef.current % 10 === 0) {
        try {
          const poseResult = poseLandmarker.detectForVideo(videoRef.current, now);
          if (poseResult.landmarks && poseResult.landmarks.length > 0) {
            const postureLabel = classifyPosture(poseResult);
            postureLabelsRef.current.push(postureLabel);

            // Track shoulder Y for respiratory effort
            const landmarks = poseResult.landmarks[0];
            const leftShoulderY = landmarks[11]?.y;
            if (leftShoulderY !== undefined) {
              shoulderYTimeSeriesRef.current.push(leftShoulderY);
            }
          }
        } catch (e) {
          console.warn('Pose detection error:', e);
        }
      }

      // Face: every 10 frames (offset by 5)
      if (frameCountRef.current % 10 === 5) {
        try {
          const faceResult = faceLandmarker.detectForVideo(videoRef.current, now);
          if (faceResult.faceLandmarks && faceResult.faceLandmarks.length > 0) {
            const landmarks = faceResult.faceLandmarks[0];
            const eyeOpen = computeEyeOpenRatio(landmarks);
            const painScore = computePainScore(landmarks);
            eyeOpenRatiosRef.current.push(eyeOpen);
            painScoresRef.current.push(painScore);
          }
        } catch (e) {
          console.warn('Face detection error:', e);
        }
      }

      // Skin: every 30 frames
      if (frameCountRef.current % 30 === 0) {
        try {
          const faceROI = extractFaceROI(canvas.width, canvas.height);
          const faceImageData = ctx.getImageData(faceROI.x, faceROI.y, faceROI.width, faceROI.height);
          const pallorScore = computePallorScore(faceImageData);
          const sweatFlag = detectSweat(faceImageData);
          pallorScoresRef.current.push(pallorScore);
          sweatFlagsRef.current.push(sweatFlag);
        } catch (e) {
          console.warn('Skin analysis error:', e);
        }
      }

      frameCountRef.current++;
      animationFrameRef.current = requestAnimationFrame(processFrame);
    };

    processFrame();
  };

  const processMeasurementData = async () => {
    setPhase('processing');

    try {
      console.log('Processing measurement data...');
      console.log('Green signal samples:', gSignalRef.current.length);
      console.log('Posture labels:', postureLabelsRef.current.length);
      console.log('Eye open ratios:', eyeOpenRatiosRef.current.length);

      // Process rPPG (for heart rate only)
      console.log('Processing rPPG for heart rate...');
      const rppgResult = processRppgSignal(gSignalRef.current, MEASUREMENT_CONFIG.TARGET_FPS);
      console.log('rPPG result:', rppgResult);

      // Process respiratory rate from shoulder movement (Pose-based)
      console.log('Processing respiratory rate from pose...');
      const respiratoryRateResult = computeRespiratoryRate(shoulderYTimeSeriesRef.current, MEASUREMENT_CONFIG.POSE_SAMPLE_RATE);
      console.log('Respiratory rate result:', respiratoryRateResult);

      // Combine HR from rPPG and RR from Pose
      // Apply default values if null: HR=70, RR=20
      setVitals({
        hr_bpm: rppgResult.hr_bpm ?? 70,
        rr_bpm: respiratoryRateResult.rr_bpm ?? 20,
        hr_confidence: rppgResult.hr_confidence,
        rr_confidence: respiratoryRateResult.confidence,
      });

      // Process posture
      console.log('Processing posture...');
      const postureResult = aggregatePostureResults(postureLabelsRef.current);
      setPosture(postureResult);
      console.log('Posture result:', postureResult);

      // Process respiratory effort
      console.log('Processing respiratory effort...');
      const respiratoryEffort = computeRespiratoryEffort(shoulderYTimeSeriesRef.current);
      setRespiratoryEffortScore(respiratoryEffort);
      console.log('Respiratory effort:', respiratoryEffort);

      // Process face features
      console.log('Processing face features...');
      const faceResult = aggregateFaceFeatures(eyeOpenRatiosRef.current, painScoresRef.current);
      console.log('Face result:', faceResult);

      // Process skin features
      console.log('Processing skin features...');
      const skinResult = aggregateSkinFeatures(pallorScoresRef.current, sweatFlagsRef.current);
      console.log('Skin result:', skinResult);

      setFaceFeatures({
        eye_open_ratio: faceResult.eye_open_ratio,
        pain_face_score: faceResult.pain_face_score,
        pallor_score: skinResult.pallor_score,
        sweat_flag: skinResult.sweat_flag,
      });

      // Cleanup
      console.log('Cleaning up...');
      disposePoseLandmarker();
      disposeFaceLandmarker();

      console.log('Measurement processing complete!');
      setPhase('results');
    } catch (error) {
      console.error('Error processing measurement data:', error);
      setError('Processing failed: ' + (error instanceof Error ? error.message : 'Unknown error'));
      setPhase('ready');
    }
  };

  const sendToTriageEngine = async () => {
    setPhase('sending');
    setError('');

    const payload: TriageRequestPayload = {
      patient_id: generatePatientId(),
      timestamp: new Date().toISOString(),
      device_info: {
        platform: detectPlatform(),
        app_version: import.meta.env.VITE_APP_VERSION || '0.1.0',
      },
      vitals,
      posture,
      respiratory_effort_score: respiratoryEffortScore,
      face: faceFeatures,
      chief_complaint: chiefComplaint,
    };

    try {
      const response = await postToOpusWithRetry(payload);

      // Navigate to results page with response data
      navigate('/result', { state: { triageResponse: response, measurements: payload } });
    } catch (error) {
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('Failed to send data. Please try again.');
      }
      setPhase('results');
    }
  };

  const detectPlatform = (): 'ios' | 'android' | 'web' => {
    const ua = navigator.userAgent;
    if (/iPhone|iPad|iPod/.test(ua)) return 'ios';
    if (/Android/.test(ua)) return 'android';
    return 'web';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-3 sm:p-4">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
          Patient Triage
        </h1>

        {error && (
          <div className="mb-4 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-lg text-sm sm:text-base text-red-800">
            {error}
          </div>
        )}

        {phase === 'idle' && (
          <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-4 sm:mb-6">
            <CameraCapture
              onStreamReady={handleStreamReady}
              onError={handleCameraError}
            />
          </div>
        )}

        {(phase === 'ready' || phase === 'measuring') && (
          <>
            <div className="bg-white rounded-lg shadow p-3 sm:p-6 mb-4 sm:mb-6 relative">
              <video
                ref={(el) => {
                  if (el && videoRef.current !== el) {
                    videoRef.current = el;
                    if (streamRef.current) {
                      el.srcObject = streamRef.current;
                    }
                    // Setup overlay canvas
                    if (overlayCanvasRef.current && el.videoWidth > 0) {
                      overlayCanvasRef.current.width = el.videoWidth;
                      overlayCanvasRef.current.height = el.videoHeight;
                    }
                  }
                }}
                autoPlay
                playsInline
                muted
                className="w-full rounded-lg bg-black"
              />
              {phase === 'measuring' && (
                <canvas
                  ref={overlayCanvasRef}
                  className="absolute top-3 sm:top-6 left-3 sm:left-6 w-[calc(100%-1.5rem)] sm:w-[calc(100%-3rem)] h-auto rounded-lg pointer-events-none"
                  style={{ objectFit: 'cover' }}
                />
              )}
            </div>

            {phase === 'measuring' && (
              <CountdownTimer
                secondsRemaining={secondsRemaining}
                totalSeconds={10}
              />
            )}

            {phase === 'ready' && (
              <>
                <div className="mb-4 sm:mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chief Complaint (optional)
                  </label>
                  <textarea
                    value={chiefComplaint}
                    onChange={(e) => setChiefComplaint(e.target.value)}
                    placeholder="Enter patient's main symptom (optional)"
                    maxLength={500}
                    rows={3}
                    className="w-full px-3 sm:px-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <button
                  onClick={startMeasurement}
                  className="w-full px-6 py-3 bg-green-600 text-white text-base sm:text-lg font-semibold rounded-lg hover:bg-green-700 active:bg-green-800 transition touch-manipulation"
                >
                  Start Measurement
                </button>

                <p className="text-xs sm:text-sm text-gray-600 mt-3 text-center">
                  Hold device steady for 10 seconds
                </p>
              </>
            )}
          </>
        )}

        {phase === 'processing' && (
          <div className="bg-white rounded-lg shadow p-8 sm:p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
            <p className="text-base sm:text-lg text-gray-700">Processing data...</p>
          </div>
        )}

        {(phase === 'results' || phase === 'sending') && (
          <>
            <VitalSummaryCard
              vitals={vitals}
              posture={posture}
              respiratoryEffortScore={respiratoryEffortScore}
              face={faceFeatures}
            />

            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <button
                onClick={() => window.location.reload()}
                className="w-full sm:flex-1 px-6 py-3 bg-gray-600 text-white text-base font-semibold rounded-lg hover:bg-gray-700 active:bg-gray-800 transition touch-manipulation"
              >
                Retake
              </button>

              <button
                onClick={sendToTriageEngine}
                disabled={phase === 'sending'}
                className="w-full sm:flex-1 px-6 py-3 bg-blue-600 text-white text-base font-semibold rounded-lg hover:bg-blue-700 active:bg-blue-800 transition disabled:bg-gray-400 touch-manipulation"
              >
                {phase === 'sending' ? 'Sending...' : 'Send to Triage Engine'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
